(() => {
  if (customElements.get('fp-v6-rail')) return;
  // Sideways product rail: native swipe/trackpad scrolling, previous/next buttons (in the heading or beside
  // the products), an optional progress line, and click-and-drag for mouse users.
  // Vertical wheel scrolling is never intercepted; it always moves the page.
  class Rail extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._rail = this.querySelector('[data-rail]');
      if (!this._rail) return;
      this._controller = new AbortController();
      const signal = this._controller.signal;
      this._buttons = [...this.querySelectorAll('[data-direction]')];
      this._controls = this.querySelector('[data-rail-controls]');
      this._progress = this.querySelector('[data-rail-progress] > span');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-direction]');
        if (!button || button.disabled || !this._rail.firstElementChild) return;
        const gap = parseFloat(getComputedStyle(this._rail).columnGap) || 0;
        const step = this._rail.firstElementChild.getBoundingClientRect().width + gap;
        const visible = Math.max(1, Math.floor(this._rail.clientWidth / step));
        this._rail.scrollBy({ left: Number(button.dataset.direction) * visible * step,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal });
      this._rail.addEventListener('scroll', () => this.update(), { passive: true, signal });
      if (this.hasAttribute('data-drag')) this.enableDrag(signal);
      this._observer = new ResizeObserver(() => this.update());
      this._observer.observe(this._rail);
      this.update();
    }
    // Mouse only: touch and trackpads already scroll natively. A drag never opens the card under the pointer.
    enableDrag(signal) {
      const rail = this._rail;
      let start = null;
      let dragged = false;
      rail.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      rail.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button')) return;
        start = { x: event.clientX, left: rail.scrollLeft };
        dragged = false;
      }, { signal });
      window.addEventListener('pointermove', (event) => {
        if (!start) return;
        const dx = event.clientX - start.x;
        if (!dragged && Math.abs(dx) < 6) return;
        if (!dragged) { dragged = true; rail.classList.add('is-dragging'); }
        rail.scrollLeft = start.left - dx;
      }, { signal });
      window.addEventListener('pointerup', () => {
        if (!start) return;
        start = null;
        if (dragged) rail.classList.remove('is-dragging'); // scroll-snap returns and settles on a card
      }, { signal });
      rail.addEventListener('click', (event) => {
        if (dragged) { event.preventDefault(); event.stopPropagation(); dragged = false; }
      }, { capture: true, signal });
    }
    update() {
      const end = Math.max(0, this._rail.scrollWidth - this._rail.clientWidth);
      const atStart = this._rail.scrollLeft <= 2;
      const atEnd = this._rail.scrollLeft >= end - 2;
      for (const button of this._buttons) {
        const forward = button.dataset.direction === '1';
        button.disabled = forward ? atEnd : atStart;
        if (button.classList.contains('v6-rail-side')) button.hidden = end < 2 || button.disabled;
      }
      if (this._controls) this._controls.hidden = end < 2;
      // keep side buttons level with the middle of the product images
      const media = this._rail.querySelector('.v6-card__media, .fp-category-card__media');
      if (media) this.style.setProperty('--rail-media-mid', `${media.offsetHeight / 2}px`);
      if (this._progress) {
        const share = this._rail.scrollWidth ? this._rail.clientWidth / this._rail.scrollWidth : 1;
        this._progress.style.width = `${Math.min(100, share * 100)}%`;
        this._progress.style.transform = `translateX(${end ? (this._rail.scrollLeft / end) * (1 / share - 1) * 100 : 0}%)`;
      }
    }
    disconnectedCallback() {
      this._controller?.abort();
      this._observer?.disconnect();
      this._controller = null;
    }
  }
  customElements.define('fp-v6-rail', Rail);
})();
